import { useState, useRef, useEffect } from "react";
import { Link } from "zudoku/components";
import { useAuth } from "zudoku/hooks";
import { LoginModalController } from "./ApiKeyPlugin";

type Featured = {
  title: string;
  description: string;
  to: string;
  color: string;
  bg: string;
  badge?: string;
  icon: JSX.Element;
};

type Item = {
  title: string;
  description: string;
  to: string;
  icon: JSX.Element;
};

const featured: Featured[] = [
  {
    title: "Get Started",
    description: "Set up your API keys and make your first payment in minutes.",
    to: "/get-started",
    color: "#2563eb",
    bg: "rgba(37,99,235,0.12)",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>
    ),
  },
  {
    title: "Accept Payments",
    description: "Checkout sessions, charges, and refunds - every way to collect.",
    to: "/checkouts",
    color: "#6366f1",
    bg: "rgba(99,102,241,0.12)",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>
    ),
  },
  {
    title: "Relay",
    description: "Collect from customers and pay out across your marketplace, keeping your commission.",
    to: "/relay",
    badge: "New",
    color: "#059669",
    bg: "rgba(5,150,105,0.12)",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/><circle cx="18" cy="5" r="3"/></svg>
    ),
  },
  {
    title: "API Reference",
    description: "Explore every endpoint with an interactive playground.",
    to: "/api",
    color: "#d97706",
    bg: "rgba(217,119,6,0.12)",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
    ),
  },
];

const sections: { label: string; items: Item[] }[] = [
  {
    label: "Get Started",
    items: [
      {
        title: "Overview",
        description: "Base URL, environments, and API conventions",
        to: "/overview",
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
        ),
      },
      {
        title: "Authentication",
        description: "Authenticate requests with your API keys",
        to: "/authentication",
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        ),
      },
      {
        title: "Errors",
        description: "Error types, codes, and how to handle them",
        to: "/errors",
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
        ),
      },
    ],
  },
  {
    label: "Guides",
    items: [
      {
        title: "Checkouts",
        description: "Hosted and embedded checkout sessions",
        to: "/checkouts",
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>
        ),
      },
      {
        title: "Webhooks",
        description: "Real-time notifications for payment events",
        to: "/webhooks",
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m13 2-2 2.5h3L12 7"/><path d="M10 14v-3"/><path d="M14 14v-3"/><path d="M11 19c-1.7 0-3-1.3-3-3v-2h8v2c0 1.7-1.3 3-3 3z"/><path d="M16 14h1a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2h-1"/><path d="M8 14H7a2 2 0 0 0-2 2v1a2 2 0 0 0 2 2h1"/></svg>
        ),
      },
      {
        title: "Filtering",
        description: "Filter and query list endpoints",
        to: "/filtering",
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
        ),
      },
      {
        title: "Pagination",
        description: "Page through large result sets",
        to: "/pagination",
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" x2="21" y1="6" y2="6"/><line x1="8" x2="21" y1="12" y2="12"/><line x1="8" x2="21" y1="18" y2="18"/><line x1="3" x2="3.01" y1="6" y2="6"/><line x1="3" x2="3.01" y1="12" y2="12"/><line x1="3" x2="3.01" y1="18" y2="18"/></svg>
        ),
      },
    ],
  },
];

function ProfileDropdown({ profile, displayName }: { profile?: { name?: string; email?: string; pictureUrl?: string } | null; displayName?: string | null }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        {profile?.pictureUrl ? (
          <img src={profile.pictureUrl} alt="" className="w-7 h-7 rounded-full object-cover" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
            {(displayName || "U").charAt(0).toUpperCase()}
          </div>
        )}
        <span className="text-sm font-medium">{displayName}</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-popover border border-border shadow-lg z-50" style={{ borderRadius: "var(--radius)" }}>
          <div className="px-3 py-2 border-b border-border">
            <p className="text-sm font-medium truncate">{displayName}</p>
            {displayName && profile?.email && displayName !== profile.email && (
              <p className="text-xs text-muted-foreground truncate">{profile.email}</p>
            )}
          </div>
          <div className="py-1">
            <Link
              to="/settings/api-keys"
              className="block px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors no-underline"
              onClick={() => setOpen(false)}
            >
              API Keys
            </Link>
          </div>
          <div className="border-t border-border py-1">
            <Link
              to="/auth/signout"
              className="block px-3 py-2 text-sm text-destructive hover:bg-accent transition-colors no-underline"
              onClick={() => setOpen(false)}
            >
              Sign Out
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function useProfileName(profile?: { name?: string; email?: string } | null) {
  // Zustand profile may be stale or empty on first render (hydration timing).
  // Fall back to our own localStorage for the display name.
  if (profile?.name) return profile.name;
  if (profile?.email) return profile.email;
  try {
    const raw = localStorage.getItem("payonify-auth");
    if (raw) {
      const auth = JSON.parse(raw);
      return auth.firstName || auth.email || null;
    }
  } catch {}
  return null;
}

export function LandingPage() {
  const { isAuthenticated, profile } = useAuth();
  const displayName = useProfileName(profile);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/">
            <img
              src="/logos/payonify-docs-light.svg"
              alt="Payonify Docs"
              className="h-6 block dark:hidden"
            />
            <img
              src="/logos/payonify-docs-dark.svg"
              alt="Payonify Docs"
              className="h-6 hidden dark:block"
            />
          </Link>
          <nav className="flex items-center gap-6 text-sm font-medium">
            {isAuthenticated ? (
              <ProfileDropdown profile={profile} displayName={displayName} />
            ) : (
              <button
                onClick={() =>
                  window.dispatchEvent(new CustomEvent("payonify:sign-in"))
                }
                className="inline-flex items-center justify-center text-sm font-medium h-8 px-4 bg-primary text-primary-foreground hover:opacity-90 transition-all"
                style={{ borderRadius: "var(--radius)" }}
              >
                Login
              </button>
            )}
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-6 py-16">
          {/* Hero */}
          <div className="text-center">
            <p className="text-sm font-semibold text-primary">Documentation</p>
            <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight">
              Payonify API Documentation
            </h1>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              One API for EcoCash, OneMoney, ZimSwitch, Visa, Mastercard and more.
              Explore guides, references, and examples to go live in hours.
            </p>
          </div>

          {/* Featured */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {featured.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="group flex flex-col items-center text-center gap-3 border border-border p-5 hover:border-primary/40 hover:shadow-sm transition-all no-underline"
                style={{ borderRadius: "var(--radius)" }}
              >
                <div
                  className="shrink-0 w-10 h-10 flex items-center justify-center"
                  style={{ backgroundColor: c.bg, color: c.color, borderRadius: "calc(var(--radius) - 2px)" }}
                >
                  {c.icon}
                </div>
                <div>
                  <div className="flex items-center justify-center gap-2">
                    <h2 className="text-base font-semibold group-hover:text-primary transition-colors">
                      {c.title}
                    </h2>
                    {c.badge && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                        {c.badge}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {c.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          {/* Sections */}
          {sections.map((s) => (
            <section key={s.label} className="mt-14">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center">
                {s.label}
              </h2>
              <div className={`mt-6 grid grid-cols-1 ${s.items.length <= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-4"} gap-3`}>
                {s.items.map((it) => (
                  <Link
                    key={it.to + it.title}
                    to={it.to}
                    className="group flex flex-col items-center text-center gap-2 p-4 hover:bg-accent transition-colors no-underline"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    <div className="text-muted-foreground group-hover:text-primary transition-colors">
                      {it.icon}
                    </div>
                    <div>
                      <h3 className="font-medium group-hover:text-primary transition-colors">
                        {it.title}
                      </h3>
                      <p className="mt-0.5 text-sm text-muted-foreground">
                        {it.description}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-6 text-center text-sm text-muted-foreground">
        &copy; {new Date().getFullYear()} Payonify. All rights reserved.
      </footer>

      {/* Login modal (needed because landing page uses layout: "none") */}
      <LoginModalController />
    </div>
  );
}
